import { getCurrentUser } from '@/lib/auth-utils'
import { ROLE_ADMIN } from '@/lib/permissions'
import { neon } from '@/services/neon'

export interface Voucher {
  id: string
  code: string
  discount_type: 'percentage' | 'fixed'
  discount_value: number
  max_uses?: number | null
  used_count: number
  expires_at?: string | null
  is_active: boolean
  created_by?: string | null
  created_at?: string
  updated_at?: string
}

function requireAdmin() {
  const user = getCurrentUser()
  if (!user || user.role !== ROLE_ADMIN) throw new Error('Not authorized — admin only')
}

export async function fetchVouchers(): Promise<Voucher[]> {
  requireAdmin()
  const { data, error } = await neon
    .from('vouchers')
    .select('*')
    .order('created_at', { ascending: false })
  if (error) throw new Error(error.message)
  return (data as Voucher[]) || []
}

export async function fetchActiveVouchers(): Promise<Voucher[]> {
  const { data, error } = await neon
    .from('vouchers')
    .select('*')
    .eq('is_active', true)
    .order('created_at', { ascending: false })
  if (error) throw new Error(error.message)
  return (data as Voucher[]) || []
}

export async function createVoucher(input: {
  code: string
  discount_type: 'percentage' | 'fixed'
  discount_value: number
  max_uses?: number | null
  expires_at?: string | null
}): Promise<Voucher> {
  requireAdmin()
  const user = getCurrentUser()
  const code = input.code.trim().toUpperCase()
  if (!code) throw new Error('Code is required')
  if (code.length < 3) throw new Error('Code must be at least 3 characters')
  if (
    input.discount_type === 'percentage' &&
    (input.discount_value <= 0 || input.discount_value > 100)
  ) {
    throw new Error('Percentage discount must be between 1 and 100')
  }
  if (input.discount_type === 'fixed' && input.discount_value <= 0) {
    throw new Error('Fixed discount must be > 0')
  }

  const { data, error } = await neon
    .from('vouchers')
    .insert({
      code,
      discount_type: input.discount_type,
      discount_value: input.discount_value,
      max_uses: input.max_uses ?? null,
      expires_at: input.expires_at || null,
      is_active: true,
      created_by: user?.id || null,
    })
    .select()
    .single()

  if (error) throw new Error(error.message)
  return data as Voucher
}

export async function updateVoucher(id: string, input: Partial<Voucher>): Promise<Voucher> {
  requireAdmin()
  const payload: Record<string, any> = {}
  if (input.code !== undefined) payload.code = input.code.trim().toUpperCase()
  if (input.discount_type !== undefined) payload.discount_type = input.discount_type
  if (input.discount_value !== undefined) payload.discount_value = input.discount_value
  if (input.max_uses !== undefined) payload.max_uses = input.max_uses
  if (input.expires_at !== undefined) payload.expires_at = input.expires_at
  if (input.is_active !== undefined) payload.is_active = input.is_active

  const { data, error } = await neon.from('vouchers').update(payload).eq('id', id).select().single()
  if (error) throw new Error(error.message)
  return data as Voucher
}

export async function deleteVoucher(id: string): Promise<void> {
  requireAdmin()
  const { error } = await neon.from('vouchers').delete().eq('id', id)
  if (error) throw new Error(error.message)
}

export async function validateVoucher(
  code: string,
  originalAmount: number,
): Promise<{
  valid: boolean
  voucher?: Voucher
  discountAmount?: number
  finalAmount?: number
  error?: string
}> {
  const cleanCode = code.trim().toUpperCase()
  if (!cleanCode) return { valid: false, error: 'Code is required' }

  const { data, error } = await neon.from('vouchers').select('*').eq('code', cleanCode).single()
  if (error || !data) {
    return { valid: false, error: 'Invalid voucher code' }
  }

  const voucher = data as Voucher
  if (!voucher.is_active) return { valid: false, error: 'Voucher is inactive' }
  if (voucher.expires_at && new Date(voucher.expires_at) < new Date()) {
    return { valid: false, error: 'Voucher has expired' }
  }
  if (
    voucher.max_uses !== null &&
    voucher.max_uses !== undefined &&
    voucher.used_count >= voucher.max_uses
  ) {
    return { valid: false, error: 'Voucher has reached max uses' }
  }

  let discountAmount = 0
  if (voucher.discount_type === 'percentage') {
    discountAmount = Math.round((originalAmount * voucher.discount_value) / 100)
  } else {
    discountAmount = Math.min(voucher.discount_value, originalAmount)
  }

  const finalAmount = Math.max(0, originalAmount - discountAmount)

  return { valid: true, voucher, discountAmount, finalAmount }
}

export async function redeemVoucher(
  voucherId: string,
  userId: string,
  invoiceId: string,
  discountAmount: number,
): Promise<void> {
  // Fetch current used_count
  const { data: voucher } = await neon
    .from('vouchers')
    .select('used_count')
    .eq('id', voucherId)
    .single()
  const currentCount = (voucher as { used_count?: number } | null)?.used_count || 0

  const { error: updateError } = await neon
    .from('vouchers')
    .update({ used_count: currentCount + 1 })
    .eq('id', voucherId)
  if (updateError) throw new Error(updateError.message)

  const { error } = await neon.from('voucher_redemptions').insert({
    voucher_id: voucherId,
    user_id: userId,
    invoice_id: invoiceId,
    discount_amount: discountAmount,
  })

  if (error) throw new Error(error.message)
}
